// deps
import React, { ReactElement, memo, useCallback } from "react";
import classNames from "classnames";

// internal deps
import { Tick } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// interfaces
import {
    FlipkartSuperCoinsPaymentMethodData,
    FlipkartSuperCoinsPaymentGroup,
    PaymentMethodProps,
} from "@payment-ui/interfaces";

// widgets
import { PaymentMethodName } from "@payment-ui/widgets/payment-method-name";
import { PaymentMethodIcon } from "@payment-ui/widgets/payment-method-icon";
import { FlipkartSuperCoinTooltip } from "@payment-ui/widgets/flipkart-super-coin-tooltip";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// helpers
import { format } from "@swiggy-private/core-ui";
import {
    canEnablePaymentMethod,
    getPaymentMethodPromoMsg,
    getPaymentMethodIconUrl,
    getPaymentMethodDisplayName,
    getPaymentMethodDiscount,
    getPaymentMethodDisableMsg,
} from "@payment-ui/helpers/Payment";

// styles
import styles from "./FlipkartSuperCoins.scss";

// test-ids
import { FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { Alert } from "@payment-ui/widgets/alert";

interface FlipkartSuperCoin extends PaymentMethodProps {
    methodData: FlipkartSuperCoinsPaymentMethodData;
    paymentActions: FlipkartSuperCoinsPaymentGroup["paymentActions"];
    paymentAmount: string;
    onFlipkartToolTipShow?: () => void;
}

function FlipkartSuperCoinPaymentMethod(props: FlipkartSuperCoin): ReactElement {
    const { methodData, paymentActions, isPaymentGroupEnabled, selected, paymentAmount, onFlipkartToolTipShow } = props;
    const { onSelect } = paymentActions;

    const isMethodEnabled = canEnablePaymentMethod(methodData, isPaymentGroupEnabled);

    const promoMessage = isMethodEnabled ? getPaymentMethodPromoMsg(methodData) : "";

    const onPay = useCallback(() => {
        if (!isMethodEnabled) {
            return;
        }
        paymentActions.onPay(methodData);
    }, [isMethodEnabled, paymentActions, methodData]);

    const onPayClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onPay();
        },
        [onPay],
    );

    const onClick = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();
            if (!isMethodEnabled) {
                return;
            }
            onSelect(methodData);
        },
        [onSelect, methodData, isMethodEnabled],
    );

    const paymentMethodContainerClass = classNames(styles.paymentMethod, {
        [styles.paymentMethodDisabled]: !isMethodEnabled,
    });

    return (
        <div className={styles.fkMainContainer}>
            {isMethodEnabled && <FlipkartSuperCoinTooltip onFlipkartToolTipShow={onFlipkartToolTipShow} />}
            <div
                className={paymentMethodContainerClass}
                onClick={onClick}
                data-testid={FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS.CONTAINER}
            >
                <div className={styles.paymentMethodContainer}>
                    <PaymentMethodIcon
                        src={getPaymentMethodIconUrl(methodData)}
                        data-testid={FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS.ICON}
                    />
                    <PaymentMethodName
                        displayName={getPaymentMethodDisplayName(methodData)}
                        promoMsg={promoMessage}
                        discount={getPaymentMethodDiscount(methodData)}
                        enabled={isMethodEnabled}
                        data-testid={FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS.NAME}
                    >
                        {!isMethodEnabled && (
                            <Alert
                                className={styles.paymentMethodDisabledText}
                                message={getPaymentMethodDisableMsg(props.methodData)}
                                data-testid={FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS.DISABLE_MESSAGE}
                            />
                        )}
                    </PaymentMethodName>
                    {isMethodEnabled && (
                        <Text
                            variant="V2.body3.neutral.regular"
                            color="text_High_Emphasis"
                            className={styles.paymentMethodBalance}
                            data-testid={FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS.PAYMENT_AMOUNT}
                        >
                            {methodData.eligibleBalance}
                        </Text>
                    )}
                    {isMethodEnabled && (
                        <Tick
                            selected={selected}
                            className={styles.paymentMethodContainerTick}
                            data-testid={FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS.TICK}
                        />
                    )}
                </div>
                {selected && isMethodEnabled && (
                    <div className={styles.paymentMethodPayWrapper}>
                        <SuperButton
                            variant="fullwidth"
                            buttonType="specialPositive"
                            buttonSize="medium"
                            onClick={onPayClick}
                            className={styles.paymentMethodPayButton}
                            data-testid={FLIPKART_COINS_PAYMENT_METHOD_TEST_IDS.PAY_BUTTON}
                        >
                            {format(TEXTS.PAY_X, paymentAmount)}
                        </SuperButton>
                    </div>
                )}
            </div>
        </div>
    );
}

export default memo(FlipkartSuperCoinPaymentMethod);
